import React, { useMemo, useRef, useState } from 'react'
import { useDrag } from '@use-gesture/react';
import { useThree } from '@react-three/fiber';
import { useDispatch } from 'react-redux';
import debounce from 'lodash.debounce';

import * as actionCreator from '../../../../../action/index';
import { const_var, params } from '../../../../../redux/reducers/reducer';
import { controls } from '../../../../../redux/reducers/common/eventReducer';

import Roof from './Roof'
import Wall from './Wall'
import Legs from './Legs'
import Storage from './Storage'
import DoorWindows from '../../../DoorWindows';
import GableFrontLeanToMeasurement from '../../../Measurements/LeanToMeasurement/GableFrontLeanToMeasurement';
import WallName from './WallName';

const GableFrontLean = ({ normalMap, envMap }) => {
    const ref = useRef();
    const dispatch = useDispatch();
    const get = useThree((state) => state.get)
    const [overlapTrussArr, setOverlapTrussArr] = useState();
    const [isDragable, setIsDragable] = useState(true);
    const [position, setPosition] = useState(0);
    const [startPosition, setStartPosition] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [previousLenght, setPreviousLenght] = useState(0);
    const [leanToTrussPosArr, setLeanToTrussPosArr] = useState([]);

    const camPosZ = const_var.camera?.position?.z;
    
    let offset = 1
    if(camPosZ >= 0) offset = 1;
    else if(camPosZ < 0) offset = -1;

    let limitRange = (params.leanF_p_h - params.p_h + params.leanF_p_w * params.b_l_t_r_pF / 24) / params.p_r_p * 12 - params.leanF_p_w / 2;

    if (params.leanStyle.front === 'standard' || limitRange < 0) {
        limitRange = 0;
    }

    const minLimit = -params.p_w / 2 + params.leanF_p_w / 2 + limitRange;
    const maxLimit = params.p_w / 2 - params.leanF_p_w / 2 - limitRange;

    const bind = useDrag(
        ({ down, delta, first, event, movement: [mx, my] }) => {
            if (controls.isCheckActiveTab === 'doorwindow' ? (const_var.focusedSection === "gablefrontlean" && params.activeWall !== "G_F_L_F_S_W") : true) {
                event.stopPropagation();
            }

            if (controls.isCheckActiveTab === 'buildsizes_front') {
                if (first) {
                    setStartPosition(position);
                }


                if (down === true) {  
                    const distance = (const_var.controls !== undefined && const_var.controls.getDistance !== undefined)?const_var.controls.getDistance([0, 0, 0]):[0, 0, 0];
                    const_var.controls.enableRotate = false;
                    get().gl.domElement.style.cursor = 'move';
                    
                    let distanceValue = distance - params.p_d / 2;

                    if (isDragable) {
                        const nextPosition = startPosition + (mx * offset) * (distanceValue / 1000);
                        if (nextPosition < minLimit) {
                            setPosition(minLimit)
                        } else if (nextPosition > maxLimit) {
                            setPosition(maxLimit)
                        } else {
                            setPosition(nextPosition);
                        }
                    }
                    setDragging(true);
                } else {
                    const_var.controls.enableRotate = true;
                    get().gl.domElement.style.cursor = 'auto'

                    dispatch(actionCreator.changeLeanDragState(true));
                    setIsDragable(false);

                    const updatedNewDoorArr = const_var.newDoorsArray.map((item) => {
                        if (item.userData.wallName === 'G_F_L_F_W' || item.userData.wallName === 'G_F_L_F_S_W' || item.userData.wallName === 'G_F_L_B_W' || item.userData.wallName === 'G_F_L_B_S_W') {
                            return {
                                ...item,
                                positionX: (item.userData.wallName === 'G_F_L_B_W' || item.userData.wallName === 'G_F_L_B_S_W') ? item.positionX - (position - startPosition) : item.positionX + (position - startPosition),
                                minLimitX: item.minLimitX + (position - startPosition),
                                maxLimitX: item.maxLimitX + (position - startPosition)
                            }
                        }
                        return item;
                    })
                    if (JSON.stringify(const_var.newDoorsArray) !== JSON.stringify(updatedNewDoorArr)) {
                        dispatch(actionCreator.updateDoorWindowsArray(updatedNewDoorArr, true));
                    }

                    dispatch(actionCreator.updateLeanToPosition('front', position))
                    if (position === minLimit) {
                        dispatch(actionCreator.changeLeantoPosition({event: 2, value: 'front'}))
                    } else if (position === maxLimit) {
                        dispatch(actionCreator.changeLeantoPosition({event: 3, value: 'front'}))
                    } else if (position === 0) {
                        dispatch(actionCreator.changeLeantoPosition({event: 1, value: 'front'}))
                    } else {
                        dispatch(actionCreator.changeLeantoPosition({event: 4, value: 'front'}))
                    }
                    
                    setPreviousLenght(`${params.p_w}${params.leanF_p_w}`)
                    setStartPosition(position);
                    setDragging(false)

                    if(position !== startPosition && !dragging){
                        setPosition(startPosition)
                    } else {
                        setPosition(position)
                    }

                    debounceForDragLean()
                }
            }
        },
        { pointerEvents: true }
    );

    const prvLeanF_p_d = useMemo(() => {
        const prvLeanF_p_d = ref.current
        ref.current = params.leanF_p_d;
        
        return prvLeanF_p_d    
    }, [params.leanF_p_d])

    useMemo(() => {
        let pos = 0;

        if( Number(params.leantoAlignmentFront) === 1 ) pos = 0;
        else if( Number(params.leantoAlignmentFront) === 2 ) pos = -params.p_w/2 + params.leanF_p_w/2 + limitRange;
        else if( Number(params.leantoAlignmentFront) === 3 ) pos = params.p_w/2 - params.leanF_p_w/2 - limitRange;
        else if( Number(params.leantoAlignmentFront) === 4) {
            if (previousLenght !== `${params.p_d}${params.leanF_p_w}` && !params.apiCallAfterEdit) {
                if (position <= minLimit) {
                    pos = minLimit;
                    dispatch(actionCreator.changeLeantoPosition({event: 2, value: 'front'}))
                } else if (position >= maxLimit) {
                    pos = maxLimit;
                    dispatch(actionCreator.changeLeantoPosition({event: 3, value: 'front'}))
                } else if (position === 0 || params.leanF_p_w === params.p_d) {
                    pos = 0;
                    dispatch(actionCreator.changeLeantoPosition({event: 1, value: 'front'}))
                } else pos = position;
            } else pos = position;
        }

        if (params.add_front_lean && !params.apiCallAfterEdit && !dragging && pos !== startPosition) {
            const sideArrayFront = const_var.newDoorsArray.filter((item) => item.userData.wallName === 'G_F_L_F_W' || item.userData.wallName === 'G_F_L_F_S_W');
            sideArrayFront.forEach((item) => {
                dispatch(actionCreator.updateDoorWindowPosition(item.uniqueId, item.positionX + (pos - startPosition)));
            })

            const sideArrayBack = const_var.newDoorsArray.filter((item) => item.userData.wallName === 'G_F_L_B_W' || item.userData.wallName === 'G_F_L_B_S_W');
            sideArrayBack.forEach((item) => {
                dispatch(actionCreator.updateDoorWindowPosition(item.uniqueId, item.positionX - (pos - startPosition)));
            })

            setStartPosition(pos);
            setPosition(pos);
            dispatch(actionCreator.updateLeanToPosition('front', pos))
        }
    }, [Number(params.leantoAlignmentFront), params.leanF_p_w, params.p_w, params.leanF_p_pos, limitRange, params.apiCallAfterEdit])

    const debounceForDragLean = debounce(() => {
        setTimeout(() => {
            setIsDragable(true);
            clearTimeout();
        }, [250]);
    }, [250]);

    useMemo(() => {
        if(params.add_front_lean) {
            dispatch(actionCreator.setLeanToTrussPosInfo({
                data: { frontLeanTo: leanToTrussPosArr }
            }));
        } else {
            dispatch(actionCreator.setLeanToTrussPosInfo({
                data: { frontLeanTo: [] }
            }));
        }
    }, [leanToTrussPosArr, params.add_front_lean]);

    useMemo(() => {
        dispatch(actionCreator.changeLeanDragState(false));
        setPosition(params.leanF_p_pos);
        setStartPosition(params.leanF_p_pos);
    }, [params.leanF_p_pos, Number(params.leantoAlignmentFront)])

    useMemo(() => {
        dispatch(actionCreator.updateGableBackWall("front"));
    }, [params.p_f_w]);

    return params.add_front_lean && (
        <group 
            name='gableFrontLean'
            position={[position,0,0]}
            {...bind()}
        >
            <Legs normalMap={normalMap} envMap={envMap} setLeanToTrussPosArr={setLeanToTrussPosArr} leanToTrussPosArr={leanToTrussPosArr}/>
            <Roof/>
            <Wall/>
            {params.add_storage_check_front && <Storage/>}
            <group position={dragging ? undefined : [-position, 0, 0]}>
                <DoorWindows overlapTrussArr={overlapTrussArr} setOverlapTrussArr={setOverlapTrussArr} section='gableFrontLean' />
            </group>
            {const_var.callMesure && 
                <>
                    <WallName />
                    <GableFrontLeanToMeasurement color='#2F7CBF' />
                </>
            }
        </group>
    )
}

export default GableFrontLean